import React, { useState, useEffect } from 'react';
import { Loader2, DollarSign, Wallet, ShieldCheck, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../../utils/axiosConfig';
import { toast } from 'sonner';

import { usePaperTrading } from '../../contexts/PaperTradingContext';

const TradePanel = ({ market, onTradeSuccess }) => {
    const { balance, executeTrade, refreshPortfolio } = usePaperTrading();
    const [side, setSide] = useState('BUY'); // BUY | SELL
    const [outcome, setOutcome] = useState('YES'); // YES | NO
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [price, setPrice] = useState(0.50);
    const [isExecuting, setIsExecuting] = useState(false);
    const [executionLogs, setExecutionLogs] = useState([]);
    const [executionProgress, setExecutionProgress] = useState(0);
    const [executionResult, setExecutionResult] = useState(null);
    const [hasPCLAW, setHasPCLAW] = useState(true);

    // Update price based on outcome
    useEffect(() => {
        if (market) {
            if (outcome === 'YES') setPrice(market.long / 100 || 0.5);
            else setPrice(1 - (market.long / 100 || 0.5));
        }
    }, [market, outcome]);

    const addLog = (msg) => {
        setExecutionLogs(prev => [...prev.slice(-3), { msg, time: new Date().toLocaleTimeString() }]);
    };

    const handleMax = () => {
        setAmount(balance.toString());
    };

    const handleTrade = async () => {
        if (!amount || parseFloat(amount) <= 0) return;
        if (parseFloat(amount) > balance && side === 'BUY') {
            toast.error("Insufficient balance");
            return;
        }

        if (!hasPCLAW) {
            toast.error("Requires 1,000 $PCLAW in wallet to cover execution fees");
            return;
        }

        setLoading(true);
        setIsExecuting(true);
        setExecutionProgress(0);
        setExecutionLogs([]);
        setExecutionResult(null);

        // Simulated steps for UI experience
        addLog("Connecting to Polymarket Agent...");
        await new Promise(r => setTimeout(r, 800));
        setExecutionProgress(25);

        addLog("Validating platform session...");
        await new Promise(r => setTimeout(r, 600));
        setExecutionProgress(50);

        addLog("Submitting order via Lobster Node...");
        await new Promise(r => setTimeout(r, 1000));
        setExecutionProgress(75);

        try {
            // Use context to execute trade
            const result = await executeTrade(
                market || { slug: "simulated-market", title: "Simulated Market" },
                parseFloat(amount),
                outcome,
                { price }
            );

            setExecutionProgress(100);
            addLog("Confirming market position...");
            await new Promise(r => setTimeout(r, 500));
            addLog("Execution successful ✓");

            // Calculate execution result for display
            const executedShares = (parseFloat(amount) / currentPrice).toFixed(2);
            const potentialWin = side === 'BUY' ? executedShares : (parseFloat(amount) * currentPrice).toFixed(2);

            setExecutionResult({
                side,
                outcome,
                amount: parseFloat(amount).toFixed(2),
                shares: executedShares,
                potentialWin,
                price: (currentPrice * 100).toFixed(1)
            });

            toast.success(`${side} ${outcome} position recorded!`);
            setAmount('');
            if (onTradeSuccess) onTradeSuccess();
        } catch (error) {
            console.error(error);
            addLog("Error: Execution rejected");
            toast.error(error.message || "Trade failed");
        } finally {
            setLoading(false);
            setTimeout(() => {
                setIsExecuting(false);
                setExecutionProgress(0);
                setTimeout(() => setExecutionResult(null), 8000);
            }, 2000);
        }
    };

    const winProb = market?.aiAnalysis?.confidence || 72;

    // UAT HIGH-007: Robust Share-based math
    const safePrice = parseFloat(market?.long || market?.probability || 50);
    const priceNum = outcome === 'YES'
        ? safePrice
        : (100 - safePrice);

    const currentPrice = Math.max(0.01, Math.min(0.99, priceNum / 100));

    const sharesNum = amount && !isNaN(parseFloat(amount)) ? (parseFloat(amount) / (side === 'BUY' ? currentPrice : 1)) : 0;
    const shares = isNaN(sharesNum) ? '0.00' : sharesNum.toFixed(2);

    const possibleWinningsValue = side === 'BUY'
        ? (parseFloat(amount || 0) / currentPrice)
        : (parseFloat(amount || 0) * currentPrice);
    const possibleWinnings = isNaN(possibleWinningsValue) ? '0.00' : possibleWinningsValue.toFixed(2);

    const potentialProfitValue = side === 'BUY'
        ? (parseFloat(amount || 0) / currentPrice) - parseFloat(amount || 0)
        : 0;

    const potentialReturn = amount && parseFloat(amount) > 0 && !isNaN(potentialProfitValue)
        ? (potentialProfitValue / parseFloat(amount)) * 100
        : 0;

    // Simulated Metrics (UAT Batch 3)
    const pclawRequired = amount ? (parseFloat(amount) * 0.05).toFixed(2) : '0';
    const executionSpeed = '0.42s';
    const slippage = '0.05%';
    const liquidity = '$4.2M';
    const maxRisk = amount ? `-$${parseFloat(amount).toFixed(2)}` : '-$0.00';
    const suggestedEntry = (balance * 0.15).toFixed(2); // 15% of balance as default suggestion

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={`bg-white/[0.02] border rounded-xl p-4 flex flex-col gap-4 transition-colors duration-500 ${isExecuting ? 'border-secondary/50 shadow-lg shadow-secondary/10' : 'border-white/10'}`}
        >
            {/* Top Metrics (UAT HIGH-006) */}
            <div className="grid grid-cols-2 gap-2 text-[10px] font-mono mb-1">
                <div className="flex flex-col">
                    <span className="text-muted-foreground uppercase tracking-tighter">Win Probability</span>
                    <span className="text-emerald-400 font-bold text-sm">{winProb}%</span>
                </div>
                <div className="flex flex-col items-end">
                    <span className="text-muted-foreground uppercase tracking-tighter">Max Risk</span>
                    <span className="text-red-400 font-bold text-sm">{maxRisk}</span>
                </div>
            </div>

            {/* Buy/Sell Tabs */}
            <div className="flex bg-white/5 p-1 rounded-lg">
                {['BUY', 'SELL'].map((s) => (
                    <motion.button
                        key={s}
                        onClick={() => setSide(s)}
                        whileTap={{ scale: 0.95 }}
                        className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${side === s
                            ? s === 'BUY' ? 'bg-emerald-500 text-black' : 'bg-red-500 text-white'
                            : 'text-muted-foreground hover:text-white'
                            }`}
                    >
                        {s}
                    </motion.button>
                ))}
            </div>

            {/* Outcome Selection */}
            <div className="flex gap-2">
                <motion.button
                    onClick={() => setOutcome('YES')}
                    whileTap={{ scale: 0.97 }}
                    className={`flex-1 py-2.5 px-3 rounded-lg border transition-all flex justify-between items-center ${outcome === 'YES'
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                        : 'bg-white/5 border-transparent hover:bg-white/10 text-muted-foreground'
                        }`}
                >
                    <span className="font-bold text-sm">YES</span>
                    <span className="text-[10px] font-mono">{(price * 100).toFixed(1)}¢</span>
                </motion.button>
                <motion.button
                    onClick={() => setOutcome('NO')}
                    whileTap={{ scale: 0.97 }}
                    className={`flex-1 py-2.5 px-3 rounded-lg border transition-all flex justify-between items-center ${outcome === 'NO'
                        ? 'bg-red-500/20 border-red-500 text-red-400'
                        : 'bg-white/5 border-transparent hover:bg-white/10 text-muted-foreground'
                        }`}
                >
                    <span className="font-bold text-sm">NO</span>
                    <span className="text-[10px] font-mono">{((1 - price) * 100).toFixed(1)}¢</span>
                </motion.button>
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
                <div className="flex justify-between text-[10px]">
                    <span className="text-muted-foreground uppercase tracking-wider font-bold">
                        {side === 'BUY' ? 'Amount (USDC)' : 'Shares to Sell'}
                    </span>
                    <span className="text-muted-foreground flex items-center gap-1 font-mono">
                        <Wallet className="w-3 h-3" />
                        ${balance.toFixed(2)}
                    </span>
                </div>
                <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        <DollarSign className="w-3 h-3" />
                    </div>
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded-lg py-2.5 pl-8 pr-14 text-sm text-white focus:outline-none focus:border-secondary/50 transition-all font-mono"
                        placeholder="0.00"
                    />
                    <button
                        onClick={handleMax}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-secondary hover:underline px-2"
                    >
                        MAX
                    </button>
                </div>
            </div>

            {/* Summary Grid (UAT HIGH-007) */}
            <div className="bg-black/40 rounded-lg p-3 space-y-2 text-[10px] border border-white/5 font-mono">
                <div className="flex justify-between">
                    <span className="text-muted-foreground uppercase">
                        {side === 'BUY' ? 'Estimated Shares' : 'Amount Received'}
                    </span>
                    <span className="text-white font-bold">
                        {side === 'BUY' ? shares : `$${possibleWinnings}`}
                    </span>
                </div>
                {side === 'BUY' && (
                    <div className="flex justify-between">
                        <span className="text-muted-foreground uppercase">To Win</span>
                        <span className="text-emerald-400 font-bold">
                            ${shares}
                        </span>
                    </div>
                )}
                <div className="flex justify-between">
                    <span className="text-muted-foreground uppercase">Potential ROI</span>
                    <span className={potentialReturn >= 0 ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>
                        {potentialReturn >= 0 ? '+' : ''}{potentialReturn.toFixed(1)}%
                    </span>
                </div>
            </div>

            {/* Simulated Metrics (UAT Batch 3) */}
            <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">PCLAW Required</p>
                    <p className="text-sm font-black text-white">{pclawRequired} PCLAW</p>
                </div>
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Execution Speed</p>
                    <p className="text-sm font-black text-secondary">{executionSpeed}</p>
                </div>
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Max Slippage</p>
                    <p className="text-sm font-black text-white">{slippage}</p>
                </div>
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Liquidity</p>
                    <p className="text-sm font-black text-white">{liquidity}</p>
                </div>
            </div>

            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary/10 border border-secondary/20">
                <ShieldCheck className="w-4 h-4 text-secondary" />
                <span className="text-[10px] font-bold text-secondary uppercase tracking-widest">Lobster Network Verified Execution</span>
            </div>

            {/* Execution Console (UAT HIGH-006) */}
            <AnimatePresence>
                {isExecuting && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-black rounded-lg p-2 border border-white/10 font-mono text-[9px] space-y-1 mb-2">
                            <div className="flex justify-between text-white/40 mb-1">
                                <span>EXECUTION CONSOLE</span>
                                <span>{executionProgress}%</span>
                            </div>
                            {executionLogs.map((log, i) => (
                                <div key={i} className="flex gap-2">
                                    <span className="text-white/20">[{log.time}]</span>
                                    <span className={log.msg.includes('✓') ? 'text-emerald-400' : 'text-cyan-400'}>{log.msg}</span>
                                </div>
                            ))}
                            <div className="w-full h-1 bg-white/5 rounded-full mt-2">
                                <motion.div
                                    className="h-full bg-secondary rounded-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${executionProgress}%` }}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Execution Result Summary */}
            <AnimatePresence>
                {executionResult && (
                    <motion.div
                        initial={{ height: 0, opacity: 0, scale: 0.95 }}
                        animate={{ height: 'auto', opacity: 1, scale: 1 }}
                        exit={{ height: 0, opacity: 0, scale: 0.95 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-gradient-to-br from-emerald-500/10 to-secondary/10 border border-emerald-500/30 rounded-xl p-4 mb-2">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Zap className="w-4 h-4 text-emerald-400" />
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Execution Complete</div>
                                    <div className="text-[9px] text-white/50 font-mono">Position successfully recorded</div>
                                </div>
                            </div>
                            <div className="space-y-2 text-[10px] font-mono">
                                <div className="flex justify-between items-center p-2 bg-black/20 rounded">
                                    <span className="text-white/60">Action:</span>
                                    <span className={`font-bold ${executionResult.side === 'BUY' ? 'text-emerald-400' : 'text-orange-400'}`}>
                                        {executionResult.side} {executionResult.outcome}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-black/20 rounded">
                                    <span className="text-white/60">Amount Invested:</span>
                                    <span className="font-bold text-white">${executionResult.amount}</span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-black/20 rounded">
                                    <span className="text-white/60">Shares Acquired:</span>
                                    <span className="font-bold text-secondary">{executionResult.shares}</span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-black/20 rounded">
                                    <span className="text-white/60">Entry Price:</span>
                                    <span className="font-bold text-white">{executionResult.price}¢</span>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-emerald-500/10 border border-emerald-500/20 rounded">
                                    <span className="text-white/60">Potential Win:</span>
                                    <span className="font-bold text-emerald-400">${executionResult.potentialWin}</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Action Button (UAT HIGH-006) */}
            <motion.button
                onClick={handleTrade}
                disabled={loading || !amount}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3 rounded-xl font-black text-xs tracking-[0.2em] uppercase transition-all flex items-center justify-center gap-2 ${loading
                    ? 'bg-secondary/20 text-secondary'
                    : 'bg-secondary hover:bg-secondary/80 text-black shadow-[0_0_20px_rgba(255,51,102,0.2)]'
                    }`}
            >
                {loading ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Execution In Progress</span>
                    </>
                ) : (
                    <>
                        <Zap className="w-4 h-4" />
                        <span>Execute Prediction</span>
                    </>
                )}
            </motion.button>

            <div className="text-[9px] text-center text-white/20 font-medium">
                Requires 1,000 $PCLAW in wallet to cover execution fees
            </div>
        </motion.div>
    );
};

export default TradePanel;
